import PropTypes from "prop-types";
import Select from "react-select";

import { COUNTRIES } from "../../constants/countries";
import { useIsMobile } from "../../hooks/useIsMobile";

/**
 * Phone input split in two controls: country dial code (searchable select)
 * and the local number. Shared by the contact form and the landing forms.
 */
const CountryPhoneField = ({
  country,
  onCountryChange,
  phone,
  onPhoneChange,
  fieldClass,
  disabled = false,
  name = "phone",
  placeholder = "Número de teléfono",
  maxLength = 100,
}) => {
  const isMobile = useIsMobile();

  return (
    <div className="flex flex-col gap-2 sm:flex-row">
      <Select
        options={COUNTRIES}
        value={country}
        onChange={onCountryChange}
        isDisabled={disabled}
        isSearchable
        placeholder="País"
        className="w-full sm:w-auto"
        classNamePrefix="country-select"
        menuPlacement="auto"
        formatOptionLabel={(option) => {
          const spaceIndex = option.label.indexOf(" ");
          const flag = option.label.slice(0, spaceIndex);
          const text = option.label.slice(spaceIndex + 1);
          return (
            <span className="flex items-center gap-1.5">
              <span style={{ fontFamily: '"Twemoji Mozilla", serif', lineHeight: 1 }}>
                {flag}
              </span>
              <span>{text}</span>
            </span>
          );
        }}
        styles={{
          control: (base, { isFocused }) => ({
            ...base,
            backgroundColor: "var(--color-input-bg)",
            border: isFocused
              ? "1px solid rgba(56, 189, 248, 0.45)"
              : "1px solid rgba(255, 255, 255, 0.10)",
            borderRadius: "0.5rem",
            boxShadow: isFocused ? "0 0 0 2px rgba(56, 189, 248, 0.12)" : "none",
            minWidth: isMobile ? "100%" : "190px",
            cursor: "pointer",
            minHeight: "44px",
            height: "44px",
            display: "flex",
            alignItems: "center",
            padding: "0 12px",
            transition: "all 0.2s ease",
            ":hover": {
              border: "1px solid rgba(56, 189, 248, 0.35)",
            },
          }),
          menu: (base) => ({
            ...base,
            backgroundColor: "var(--color-black-100)",
            borderRadius: "0.5rem",
            border: "1px solid rgba(255, 255, 255, 0.10)",
            zIndex: 50,
            minWidth: "260px",
          }),
          menuList: (base) => ({
            ...base,
            padding: "4px",
            maxHeight: "220px",
          }),
          option: (base, { isFocused, isSelected }) => ({
            ...base,
            backgroundColor: isSelected
              ? "#0369a1"
              : isFocused
              ? "rgba(255,255,255,0.08)"
              : "transparent",
            color: isSelected ? "#fff" : "var(--color-foreground)",
            borderRadius: "0.375rem",
            cursor: "pointer",
            fontSize: "0.875rem",
          }),
          singleValue: (base) => ({
            ...base,
            color: "var(--color-foreground)",
            fontSize: "0.875rem",
          }),
          input: (base) => ({
            ...base,
            color: "var(--color-foreground)",
            fontSize: "0.875rem",
          }),
          placeholder: (base) => ({
            ...base,
            color: "var(--color-secondary)",
            fontSize: "0.875rem",
          }),
          dropdownIndicator: (base) => ({
            ...base,
            color: "var(--color-secondary)",
            "&:hover": { color: "var(--color-foreground)" },
          }),
          indicatorSeparator: () => ({ display: "none" }),
        }}
      />
      <input
        id={name}
        type="tel"
        name={name}
        value={phone}
        onChange={onPhoneChange}
        maxLength={maxLength}
        placeholder={placeholder}
        autoComplete="tel"
        className={`w-full min-w-0 flex-1 ${fieldClass}`}
      />
    </div>
  );
};

CountryPhoneField.propTypes = {
  country: PropTypes.object,
  onCountryChange: PropTypes.func.isRequired,
  phone: PropTypes.string.isRequired,
  onPhoneChange: PropTypes.func.isRequired,
  fieldClass: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  maxLength: PropTypes.number,
};

export default CountryPhoneField;
